import { useTranslation } from 'react-i18next';
import React, { ReactNode, useMemo, useCallback, useRef } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import {
  Popup,
  HelperTooltip,
  UserAvatar,
  FollowButton,
  SocialTag,
  OfficialInfoCard,
  VipLogoWithGift,
  Tips,
} from 'components';
import { IconUserCardTVF } from '@/assets/userCard';
import { TopTokens, TopProtocols, TopCollections } from './components/Stats';
import { Badges } from './components/Badge';
import { CopyImage } from '@/components/shared';
import { PopupPosition } from '@/components/Popup/types';
import {
  useCurrentAddressInfo,
  useHiUserCache,
  useDeviceTypeIsMobile,
  useUserBanner,
  useTheme,
} from '@/hooks';
import {
  ProfileUSDValue,
  abbreviateNumber,
  getUserNameByUserInfo,
  shortenAddress,
  isOfficalAddress,
  convertOfficialHiIdToOriginalId,
  openNewWindow,
  login,
} from 'utils';
import style from './index.module.css';
import cx from 'clsx';
import { useUserInfo } from '@/hooks';
import { useUserBadges } from '@/views/Badge';
import { useHiConversationModal } from '@/views/Chats/components/HiConversationModal';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';
import { formatHeaderInfoNum } from '@/views/Profile/utils';

type UserCardProps = {
  address: string;
  hideSayHiButton?: boolean;
  forceCloseCallback?: () => void;
  restParams?: Record<string, string | number | boolean | undefined>;
  followCallback?: (isFollowing: boolean) => void;
  disabled?: boolean;
};

const UserInfoCard = React.memo(
  ({ address, hideSayHiButton, restParams, forceCloseCallback, followCallback }: UserCardProps) => {
    const { t } = useTranslation();

    const { address: currentAddress } = useCurrentAddressInfo();
    const isCurrentUser = useMemo(() => currentAddress === address, [currentAddress, address]);

    const history = useHistory();

    const { data: userInfoData } = useUserInfo(address);
    const { userDict } = useHiUserCache();
    const userInfo = (userDict[address] as UserInfo) || userInfoData;
    const { data: badges } = useUserBadges(address);
    const { openHiModal } = useHiConversationModal();
    const userDesc = userInfo?.desc;
    const isContract = useMemo(
      () =>
        Object.values(userDesc?.protocol || {}).length +
          Object.values(userDesc?.contract || {}).length >
        0,
      [userDesc?.protocol, userDesc?.contract],
    );
    const isSuicide = useMemo(() => userInfo?.is_suicide, [userInfo?.is_suicide]);
    const { isDark } = useTheme();

    const { data: bannerData } = useUserBanner(address);

    const multisigContract = Object.values(userDesc?.contract || {}).find(item => item.multisig);
    const isHiDisabled = isContract || multisigContract;

    const onClickSayHi = useCallback(() => {
      forceCloseCallback && forceCloseCallback();
      openHiModal({
        hiId: address,
      });
    }, [address, forceCloseCallback, openHiModal]);

    const handleAskProClick = useCallback(() => {
      forceCloseCallback?.();
      if (!address) {
        login();

        return;
      }
      history.push('/new-stream?tab=qa', { target: userInfoData });
    }, [history, userInfoData, address, forceCloseCallback]);

    const topTokens = useMemo(() => {
      return [...(userInfo?.stats?.top_coins || []), ...(userInfo?.stats?.top_tokens || [])];
    }, [userInfo?.stats?.top_coins, userInfo?.stats?.top_tokens]);

    return (
      <div className={style.userCard}>
        {bannerData?.banner_url ? (
          <div className={style.bannerBox}>
            <img className={style.banner} src={bannerData?.banner_url} alt="banner" />
            <div className={cx(style.linearBg, isDark && style.isDark)} />
          </div>
        ) : null}

        <div className={style.userCardInner}>
          <div className={style.header}>
            <UserAvatar data={userInfo?.desc} size={36} />
            <div className={style.headerRight}>
              {isSuicide ? (
                <SuicideTag
                  userAddress={userInfo?.id}
                  onClick={() => openNewWindow(`/profile/${address}`, '_blank')}
                />
              ) : (
                <span
                  className={style.userName}
                  onClick={() => openNewWindow(`/profile/${address}`, '_blank')}>
                  <span className={style.name}>{getUserNameByUserInfo(userInfo, true)}</span>

                  {userInfo?.is_vip && userInfo?.web3_id ? (
                    <VipLogoWithGift marginLeft={4} marginRight={0} userInfo={userInfo} />
                  ) : null}
                  <span className={style.memo}>{userInfo?.desc?.user?.memo}</span>
                </span>
              )}

              <div className={style.address}>
                <span className={style.addressText}>{shortenAddress(address)}</span>
                <HelperTooltip inParent title={t('UserCardPopup.copy')} placement="top">
                  <div className={style.icon}>
                    <CopyImage value={address} />
                  </div>
                </HelperTooltip>
              </div>
            </div>
            {badges?.badge_list?.length ? (
              <Badges className={style.badges} data={badges.badge_list} />
            ) : null}
          </div>
          {(userInfo?.desc?.user?.web3_id || isCurrentUser) && userInfo?.bio ? (
            <div className={style.userBio}>{userInfo?.bio}</div>
          ) : null}

          <div className={style.netWorthInfo}>
            <div className={style.left}>
              <div className={style.title}>{t('UserCardPopup.pp2nez')}</div>
              <div className={style.worth}>{ProfileUSDValue(userInfo?.desc?.usd_value)}</div>
              {userInfo ? <SocialTag addrInfo={userInfo} /> : null}
            </div>
            {isCurrentUser ? null : (
              <div className={style.right}>
                <FollowButton
                  className={style.followButton}
                  address={address}
                  restParams={restParams}
                  userInfo={userInfo}
                  operateCallback={followCallback}
                />

                <div className={style.actionBtns}>
                  {hideSayHiButton || isHiDisabled ? null : (
                    <div className={style.sayHiBtn} onClick={onClickSayHi}>
                      <div className={style.inner}>{t('UserCardPopup.14u8n86')}</div>
                    </div>
                  )}
                  {userInfoData?.enable_pay_qa ? (
                    <div className={style.viewQa} onClick={handleAskProClick}>
                      <div className={style.viewQaTxt}>AskPro</div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className={style.social}>
            {userInfo?.tvf ? (
              <HelperTooltip
                title={t('UserCardPopup.owsomm')}
                inParent
                placement="topLeft"
                className={cx(style.socialItem, style.tvf)}>
                <IconUserCardTVF className={style.icon} width={10} height={10} />
                <span className={style.socialItemTitle}>TVF</span>
                <span className={style.socialNum}>
                  ${abbreviateNumber(userInfo?.tvf || 0, 1)?.toLocaleLowerCase()}
                </span>
              </HelperTooltip>
            ) : null}
            {userInfo?.follower_count ? (
              <NavLink
                className={style.socialItem}
                target={'_blank'}
                to={`/profile/${address}/follower`}>
                <div className={style.socialItemTitle}>{t('UserCardPopup.19t454l')}</div>
                <span className={style.socialNum}>
                  {abbreviateNumber(userInfo?.follower_count)}
                </span>
              </NavLink>
            ) : null}
            {userInfo?.reward ? (
              <div className={style.socialItem}>
                <div className={style.socialItemTitle}>
                  <Tips title={t('UserCardPopup.13qfagc')} ghost className={style.titleTipIcon} />
                  {t('UserCardPopup.1mqp9zd')}
                </div>
                <span className={style.socialNum}>
                  ${userInfo?.reward ? formatHeaderInfoNum(userInfo?.reward / 10000, 2) : 0}
                </span>
              </div>
            ) : null}
          </div>

          <div className={style.statsRow}>
            <div className={style.statsTitle}>{t('UserCardPopup.1ekt1be')}</div>
            <div className={style.topStats}>
              {topTokens.length ? <TopTokens data={topTokens} /> : null}

              {userInfo?.stats?.top_protocols?.length ? (
                <TopProtocols data={userInfo?.stats.top_protocols || []} />
              ) : null}

              {userInfo?.stats?.top_collections?.length ? (
                <TopCollections data={userInfo.stats.top_collections} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

type UserCardPopupProps = UserCardProps & {
  children: ReactNode;
  className?: string;
  position?: PopupPosition[];
  address?: string;
};

export type UserCardPopupPropsPartial = Partial<UserCardProps> | true;

export const UserCardPopup = React.memo(
  ({
    children,
    address,
    className,
    position,
    restParams,
    hideSayHiButton,
    followCallback,
    disabled,
  }: UserCardPopupProps) => {
    const triggerContent = <div className={className}>{children}</div>;
    const isOfficial = useMemo(() => isOfficalAddress(address), [address]);
    const isMobile = useDeviceTypeIsMobile();

    const popupRef = useRef<any>(null);
    const forceClosePopup = useCallback(() => {
      popupRef?.current?.close();
    }, []);

    if (!address || isMobile) {
      return triggerContent;
    }
    return (
      <>
        <Popup
          on="hover"
          mouseEnterDelay={100}
          mouseLeaveDelay={300}
          ref={popupRef}
          arrow={false}
          disabled={!address || disabled}
          position={
            [
              'bottom left',
              'bottom center',
              'bottom right',
              'top left',
              'top center',
              'top right',
            ] || position
          }
          className="userCardOverlay"
          offsetY={10}
          trigger={triggerContent}>
          {isOfficial && address ? (
            <OfficialInfoCard
              id={convertOfficialHiIdToOriginalId(String(address))}
              restParams={restParams}
              forceCloseCallback={forceClosePopup}
              hideSayHiButton={hideSayHiButton}
              followCallback={followCallback}
            />
          ) : address ? (
            <UserInfoCard
              address={address}
              forceCloseCallback={forceClosePopup}
              restParams={restParams}
              hideSayHiButton={hideSayHiButton}
              followCallback={followCallback}
            />
          ) : null}
        </Popup>
      </>
    );
  },
);
